import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { loadSeoMap, SeoRecord } from "../services/platformSource";

/**
 * Per-page metadata.
 *
 * Before this existed the whole site served one <title> and one description from
 * public/index.html, so every blog post, service and product page was identical
 * to a crawler — and every link pasted into LinkedIn or Slack previewed as
 * "MindSyncX — AI-driven cognitive synchronization and automation". For a
 * company whose sales motion runs on shared links, that was the most expensive
 * defect on the site.
 *
 * Every route should render exactly one <Seo>. Nesting two means the innermost
 * wins for each individual tag, which is a confusing way to lose a canonical.
 */

const SITE_NAME = "MindSyncX";
const SITE_URL = "https://mindsyncx.com";
const DEFAULT_IMAGE = `${SITE_URL}/assets/imgs/logo/logo.svg`;

export interface SeoProps {
    /** Page title, without the site name — this component appends it. */
    title: string;
    description: string;
    /** Path only, e.g. "/products/vass-vision". Used for canonical and og:url. */
    path?: string;
    /** Absolute URL, or a site-relative path. Falls back to the logo. */
    image?: string;
    /** "article" for blog posts, so platforms render them as such. */
    type?: "website" | "article";
    /** Keeps a page out of search results — thank-you pages, the card viewer. */
    noIndex?: boolean;
    /** JSON-LD. Rendered as-is, so it must be a valid schema.org object. */
    structuredData?: Record<string, unknown>;
    /** Breadcrumb trail, innermost last. Emitted as BreadcrumbList. */
    breadcrumbs?: { name: string; path: string }[];
}

/**
 * CMS records are fetched once per page load and shared, rather than each <Seo>
 * fetching for itself. The map is small — one record per route — and metadata
 * must not cost a request per navigation.
 */
let seoCache: Record<string, SeoRecord> | null = null;
let seoPromise: Promise<Record<string, SeoRecord>> | null = null;

const getSeoMap = (): Promise<Record<string, SeoRecord>> => {
    if (seoCache) return Promise.resolve(seoCache);
    if (!seoPromise) {
        seoPromise = loadSeoMap().then((map) => {
            seoCache = map;
            return map;
        });
    }
    return seoPromise;
};

const absolute = (value: string | undefined, fallback: string): string => {
    if (!value) return fallback;
    return value.startsWith("http") ? value : `${SITE_URL}${value}`;
};

const Seo: React.FC<SeoProps> = ({
    title,
    description,
    path,
    image,
    type = "website",
    noIndex = false,
    structuredData,
    breadcrumbs,
}) => {
    // CMS values override the props, which are the compiled-in defaults. An
    // unreachable CMS therefore leaves the page's own metadata intact rather
    // than stripping it.
    const [override, setOverride] = useState<SeoRecord | null>(null);

    useEffect(() => {
        let cancelled = false;
        if (!path) return;
        getSeoMap().then((map) => {
            if (!cancelled && map[path]) setOverride(map[path]);
        });
        return () => {
            cancelled = true;
        };
    }, [path]);

    title = override?.title ?? title;
    description = override?.description ?? description;
    image = override?.image || image;
    noIndex = override?.noIndex ?? noIndex;

    const faqs = override?.faqs ?? [];

    const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
    const url = path ? `${SITE_URL}${path}` : SITE_URL;
    const imageUrl = absolute(image, DEFAULT_IMAGE);

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {/* Canonical matters here because the catalog takes query params —
                /products?category=… must not compete with /products in search. */}
            <link rel="canonical" href={url} />
            {noIndex && <meta name="robots" content="noindex, nofollow" />}

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={imageUrl} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={imageUrl} />

            {structuredData && (
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            )}

            {/* FAQPage is what earns a result in an answer engine or an AI
                overview — those surfaces quote a question and its answer rather
                than ranking a page. Emitted only when the CMS has questions for
                this route; empty FAQ markup is a spam signal. */}
            {faqs.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: faqs.map((f) => ({
                            "@type": "Question",
                            name: f.question,
                            acceptedAnswer: { "@type": "Answer", text: f.answer },
                        })),
                    })}
                </script>
            )}

            {breadcrumbs && breadcrumbs.length > 0 && (
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: breadcrumbs.map((b, i) => ({
                            "@type": "ListItem",
                            position: i + 1,
                            name: b.name,
                            item: `${SITE_URL}${b.path}`,
                        })),
                    })}
                </script>
            )}
        </Helmet>
    );
};

export const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "MindSyncX Labs",
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description:
        "Enterprise AI and full-stack engineering. Production systems across autonomous agents, computer vision, voice, business messaging, infrastructure telemetry and hospitality operations.",
};

export const articleSchema = (args: {
    headline: string;
    description: string;
    image?: string;
    datePublished?: string;
    path: string;
}) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: args.headline,
    description: args.description,
    image: absolute(args.image, DEFAULT_IMAGE),
    datePublished: args.datePublished,
    mainEntityOfPage: `${SITE_URL}${args.path}`,
    publisher: {
        "@type": "Organization",
        name: "MindSyncX Labs",
        logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    },
});

export const softwareSchema = (args: {
    name: string;
    description: string;
    category: string;
    path: string;
}) => ({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: args.name,
    description: args.description,
    applicationCategory: args.category,
    operatingSystem: "Web",
    url: `${SITE_URL}${args.path}`,
    publisher: { "@type": "Organization", name: "MindSyncX Labs" },
});

export default Seo;
